import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { buildCategoryBreadcrumb } from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'frontend');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const category = getCategoryBySlug(activeSlug);
  const articles = getArticlesByCategory(activeSlug);

  return (
    <>
      <ScrollToTop />
      <title>{category ? `${category.name} - 1024社区` : '技术分类 - 1024社区'}</title>
      <meta name="description" content={category?.description || '浏览1024社区的技术文章分类'} />
      <link rel="canonical" href={`https://jxsbzl.cn/category/${activeSlug}`} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={buildCategoryBreadcrumb(activeSlug)} />

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className={`${category?.icon || 'ri-folder-line'} text-primary-600 text-lg`}></i>
                </span>
                <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900">
                  {category?.name || '技术分类'}
                </h1>
              </div>
              <p className="text-foreground-600 text-base">
                {category?.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-300 p-5 md:p-6"
                >
                  <div className="flex items-center gap-2.5 mb-3">
                    <span className="text-xs text-foreground-500">{article.publishDate}</span>
                    <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
                  </div>
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {article.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-background-200/70">
                    <div className="flex items-center gap-2">
                      <img
                        src={article.authorAvatar}
                        alt={article.author}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-xs text-foreground-600 whitespace-nowrap">{article.author}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-chat-3-line"></i> {article.commentCount}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {articles.length === 0 && (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                  <i className="ri-inbox-line text-foreground-400 text-2xl"></i>
                </span>
                <p className="text-foreground-500 text-base">该分类暂无文章</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}